import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import '../styles/Flappy.css';

function findKingSquare(game, color) {
  try {
    const board = game.board();
    for (let r = 0; r < 8; r += 1) {
      for (let c = 0; c < 8; c += 1) {
        const piece = board[r][c];
        if (!piece) continue;
        if (piece.type === 'k' && piece.color === color) {
          const file = String.fromCharCode('a'.charCodeAt(0) + c);
          const rank = String(8 - r);
          return `${file}${rank}`;
        }
      }
    }
  } catch {
    // ignore
  }
  return null;
}

function isPromotionMove(game, fromSq, toSq) {
  try {
    const legal = game.moves({ square: fromSq, verbose: true });
    return legal.some((m) => m.to === toSq && m.promotion);
  } catch {
    return false;
  }
}

function formatMovePairs(history) {
  const pairs = [];
  for (let i = 0; i < history.length; i += 2) {
    pairs.push({
      moveNumber: Math.floor(i / 2) + 1,
      white: history[i] || '',
      black: history[i + 1] || '',
    });
  }
  return pairs;
}

export default function ChessPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const botEnabled = params.get('bot') === '1';
  const botLevel = Math.max(1, Math.min(3, Number(params.get('level') || 1)));
  const rawBotName = String(params.get('botName') || '').trim();
  const botName = rawBotName ? rawBotName.slice(0, 24) : 'QueenOracle';
  const playerColor = 'w';
  const botColor = 'b';

  const gameRef = useRef(new Chess());
  const [fen, setFen] = useState(() => gameRef.current.fen());
  const [orientation, setOrientation] = useState('white');
  const [error, setError] = useState('');
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalTargets, setLegalTargets] = useState([]);
  const [lastMoveSquares, setLastMoveSquares] = useState(null); // { from, to }
  const [pendingPromotion, setPendingPromotion] = useState(null); // { from, to }
  const [botThinking, setBotThinking] = useState(false);
  const botMoveTimeoutRef = useRef(null);
  const workerRef = useRef(null);
  const movesStripRef = useRef(null);

  const historySan = gameRef.current.history();

  const status = (() => {
    const g = gameRef.current;
    if (g.isCheckmate()) return `Checkmate — ${g.turn() === 'w' ? 'Black' : 'White'} wins`;
    if (g.isStalemate()) return 'Stalemate — draw';
    if (g.isInsufficientMaterial()) return 'Draw — insufficient material';
    if (g.isThreefoldRepetition()) return 'Draw — threefold repetition';
    if (g.isDraw()) return 'Draw';

    const side = g.turn() === 'w' ? 'White' : 'Black';
    if (g.isCheck()) return `${side} to move — check!`;
    return `${side} to move`;
  })();

  const onDrop = (sourceSquare, targetSquare) => {
    try {
      setError('');
      const g = gameRef.current;
      if (botEnabled && g.turn() !== playerColor) return false;
      if (isPromotionMove(g, sourceSquare, targetSquare)) {
        setPendingPromotion({ from: sourceSquare, to: targetSquare });
        return true;
      }

      let move;
      try {
        move = g.move({ from: sourceSquare, to: targetSquare, promotion: 'q' });
      } catch {
        move = null;
      }
      if (!move) {
        setError('Illegal move');
        return false;
      }
      setFen(g.fen());
      setSelectedSquare(null);
      setLegalTargets([]);
      setLastMoveSquares({ from: sourceSquare, to: targetSquare });
      return true;
    } catch (e) {
      setError(e?.message || 'Illegal move');
      return false;
    }
  };

  const computeLegalTargets = (fromSquare) => {
    const g = gameRef.current;
    const moves = g.moves({ square: fromSquare, verbose: true });
    return Array.from(new Set(moves.map((m) => m.to)));
  };

  const onSquareClick = (square) => {
    try {
      setError('');
      const g = gameRef.current;
      if (botEnabled && g.turn() !== playerColor) return;
      if (pendingPromotion) return;

      if (selectedSquare) {
        if (square === selectedSquare) {
          setSelectedSquare(null);
          setLegalTargets([]);
          return;
        }

        if (isPromotionMove(g, selectedSquare, square)) {
          setPendingPromotion({ from: selectedSquare, to: square });
          setSelectedSquare(null);
          setLegalTargets([]);
          return;
        }

        let attempted;
        try {
          attempted = g.move({ from: selectedSquare, to: square, promotion: 'q' });
        } catch {
          attempted = null;
        }

        if (attempted) {
          setFen(g.fen());
          setSelectedSquare(null);
          setLegalTargets([]);
          setLastMoveSquares({ from: selectedSquare, to: square });
          return;
        }

        const piece = g.get(square);
        const turn = g.turn();
        if (piece && piece.color === turn) {
          setSelectedSquare(square);
          setLegalTargets(computeLegalTargets(square));
          return;
        }
        setError('Illegal move');
        return;
      }

      const piece = g.get(square);
      const turn = g.turn();
      if (!piece) return;
      if (piece.color !== turn) return;
      setSelectedSquare(square);
      setLegalTargets(computeLegalTargets(square));
    } catch (e) {
      setError(e?.message || 'Action failed');
    }
  };

  const newGame = () => {
    setError('');
    gameRef.current = new Chess();
    setFen(gameRef.current.fen());
    setSelectedSquare(null);
    setLegalTargets([]);
    setLastMoveSquares(null);
    setPendingPromotion(null);
  };

  useEffect(() => {
    if (botEnabled) {
      workerRef.current = new Worker(new URL('../workers/chess-bot.worker.js', import.meta.url));
      workerRef.current.onmessage = (e) => {
        const { move, error: workerError } = e.data;
        setBotThinking(false);
        if (workerError) {
          setError(`Bot Error: ${workerError}`);
          return;
        }
        if (move) {
          const g = gameRef.current;
          g.move({ from: move.from, to: move.to, promotion: move.promotion || 'q' });
          setFen(g.fen());
          setLastMoveSquares({ from: move.from, to: move.to });
        }
      };
    }
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [botEnabled]);

  const runBotMove = () => {
    const g = gameRef.current;
    if (!botEnabled || !workerRef.current) return;
    if (g.isGameOver()) return;
    if (g.turn() !== botColor) return;

    setBotThinking(true);
    workerRef.current.postMessage({
      fen: g.fen(),
      botLevel,
      botColor,
    });
  };

  useEffect(() => {
    if (!botEnabled) return undefined;
    const g = gameRef.current;
    if (g.isGameOver() || pendingPromotion) return undefined;
    if (g.turn() !== botColor) return undefined;
    if (botMoveTimeoutRef.current) return undefined;

    botMoveTimeoutRef.current = setTimeout(() => {
      botMoveTimeoutRef.current = null;
      runBotMove();
    }, 350);

    return () => {
      if (botMoveTimeoutRef.current) {
        clearTimeout(botMoveTimeoutRef.current);
        botMoveTimeoutRef.current = null;
      }
    };
  }, [botEnabled, fen, pendingPromotion]);

  const undo = () => {
    setError('');
    const g = gameRef.current;
    g.undo();
    setFen(g.fen());
    setSelectedSquare(null);
    setLegalTargets([]);
    setLastMoveSquares(null);
    setPendingPromotion(null);
  };

  const flip = () => {
    setOrientation((o) => (o === 'white' ? 'black' : 'white'));
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore if clipboard not available
    }
  };

  const movePairs = useMemo(() => formatMovePairs(historySan), [historySan]);

  useEffect(() => {
    const el = movesStripRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [historySan.length]);

  const isGameOver = gameRef.current.isGameOver();
  const currentTurn = gameRef.current.turn();

  const topPlayer = orientation === 'white'
    ? { piece: '♚', name: botEnabled ? `${botName} (Lv${botLevel})` : 'Black', turnChar: 'b' }
    : { piece: '♔', name: 'You (White)', turnChar: 'w' };
  const bottomPlayer = orientation === 'white'
    ? { piece: '♔', name: 'You (White)', turnChar: 'w' }
    : { piece: '♚', name: botEnabled ? `${botName} (Lv${botLevel})` : 'Black', turnChar: 'b' };

  const checkSquare = (() => {
    const g = gameRef.current;
    try {
      if (!g.isCheck()) return null;
      return findKingSquare(g, g.turn());
    } catch {
      return null;
    }
  })();

  const customSquareStyles = useMemo(() => {
    const styles = {};
    if (selectedSquare) {
      styles[selectedSquare] = {
        background: 'radial-gradient(circle, rgba(250,204,21,0.35) 0%, rgba(250,204,21,0.18) 70%, rgba(250,204,21,0) 72%)',
      };
    }
    for (const sq of legalTargets) {
      styles[sq] = {
        background: 'radial-gradient(circle, rgba(16,185,129,0.28) 0%, rgba(16,185,129,0.14) 70%, rgba(16,185,129,0) 72%)',
      };
    }

    if (lastMoveSquares) {
      styles[lastMoveSquares.from] = {
        ...(styles[lastMoveSquares.from] || {}),
        boxShadow: 'inset 0 0 0 3px rgba(59,130,246,0.35)',
      };
      styles[lastMoveSquares.to] = {
        ...(styles[lastMoveSquares.to] || {}),
        boxShadow: 'inset 0 0 0 3px rgba(59,130,246,0.55)',
      };
    }

    if (checkSquare) {
      styles[checkSquare] = {
        ...(styles[checkSquare] || {}),
        boxShadow: 'inset 0 0 0 3px rgba(239,68,68,0.7)',
      };
    }
    return styles;
  }, [selectedSquare, legalTargets, lastMoveSquares, checkSquare]);

  return (
    <div className="chess-match-page chess-match-page--chess">
      <header className="cm-topbar">
        <button className="cm-icon-btn" onClick={() => navigate('/chess')} title="Lobby">
          <span>←</span>
        </button>
        <div className="cm-topbar-center">
          <span className="cm-topbar-title">♟ Chess vs {botName}</span>
          {botEnabled && <span className="cm-badge">Level {botLevel}</span>}
        </div>
        <button className="cm-icon-btn" onClick={() => navigate('/dashboard')} title="Dashboard">
          <span>⌂</span>
        </button>
      </header>

      {error && <div className="cm-toast cm-toast--error">{error}</div>}

      <div className={`cm-player-bar${currentTurn === topPlayer.turnChar ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">{topPlayer.piece}</span>
          <span className="cm-player-bar__name">{topPlayer.name}</span>
        </div>
        {botThinking && currentTurn === topPlayer.turnChar && <span className="cm-badge">Thinking…</span>}
      </div>

      <div className="cm-board-wrap">
        <Chessboard
          position={fen}
          onPieceDrop={onDrop}
          onSquareClick={onSquareClick}
          boardOrientation={orientation}
          areArrowsAllowed
          showBoardNotation
          customSquareStyles={customSquareStyles}
          customDarkSquareStyle={{ backgroundColor: '#779952' }}
          customLightSquareStyle={{ backgroundColor: '#edeed1' }}
        />

        {pendingPromotion && (
          <div className="cm-promo-overlay">
            <div className="cm-promo-dialog">
              <span className="cm-promo-title">Promote to:</span>
              <div className="cm-promo-options">
                {[
                  { p: 'q', label: '♛', title: 'Queen' },
                  { p: 'r', label: '♜', title: 'Rook' },
                  { p: 'b', label: '♝', title: 'Bishop' },
                  { p: 'n', label: '♞', title: 'Knight' },
                ].map((opt) => (
                  <button
                    key={opt.p}
                    className="cm-promo-btn"
                    title={opt.title}
                    onClick={() => {
                      try {
                        const g = gameRef.current;
                        const mv = g.move({ from: pendingPromotion.from, to: pendingPromotion.to, promotion: opt.p });
                        if (!mv) {
                          setError('Illegal move');
                          return;
                        }
                        setFen(g.fen());
                        setLastMoveSquares({ from: pendingPromotion.from, to: pendingPromotion.to });
                        setPendingPromotion(null);
                      } catch {
                        setError('Illegal move');
                      }
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
                <button className="cm-promo-btn cm-promo-btn--cancel" onClick={() => setPendingPromotion(null)}>✕</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={`cm-player-bar${currentTurn === bottomPlayer.turnChar ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">{bottomPlayer.piece}</span>
          <span className="cm-player-bar__name">{bottomPlayer.name}</span>
        </div>
        {botThinking && currentTurn === bottomPlayer.turnChar && <span className="cm-badge">Thinking…</span>}
      </div>

      {isGameOver && (
        <div className={`cm-result-banner${
          gameRef.current.isCheckmate() && currentTurn === 'b'
            ? ' cm-result-banner--win'
            : gameRef.current.isCheckmate()
              ? ' cm-result-banner--loss'
              : ' cm-result-banner--draw'
        }`}>
          <span className="cm-result-banner__icon">
            {gameRef.current.isCheckmate() && currentTurn === 'b' ? '🏆' : gameRef.current.isCheckmate() ? '💔' : '🤝'}
          </span>
          <span>{status}</span>
        </div>
      )}

      <div className="cm-moves-strip" ref={movesStripRef}>
        {movePairs.length === 0 && <span className="cm-moves-empty">No moves yet</span>}
        {movePairs.map((p) => (
          <div key={`mp-${p.moveNumber}`} className="cm-moves-pair">
            <span className="cm-moves-num">{p.moveNumber}.</span>
            <span className="cm-moves-san">{p.white}</span>
            {p.black && <span className="cm-moves-san cm-moves-san--black">{p.black}</span>}
          </div>
        ))}
      </div>

      <div className="cm-status-line">{status}</div>

      <div className="cm-actions">
        <button className="cm-btn cm-btn--primary" onClick={newGame}>New Game</button>
        <button className="cm-btn cm-btn--ghost" onClick={undo} disabled={historySan.length === 0}>Undo</button>
        <button className="cm-btn cm-btn--ghost" onClick={flip}>⇅ Flip</button>
        <button className="cm-btn cm-btn--ghost" onClick={() => copyText(fen)}>📋 FEN</button>
        <button className="cm-btn cm-btn--ghost" onClick={() => copyText(gameRef.current.pgn())}>📋 PGN</button>
      </div>
    </div>
  );
}
